/**
 * Envelope de convite: como a PRIMEIRA mensagem viaja.
 *
 * O quadro normal (docs/03) so existe depois que os dois lados tem sessao.
 * Mas quem inicia precisa entregar, junto do primeiro texto, o material do
 * X3DH para o outro lado refazer o acordo — e isso nao cabe num quadro, que
 * ja pressupoe a sessao pronta. Dai este envelope, que carrega os dois:
 *
 *   0    1   versao = 1
 *   1    1   marca = MessageType.Invite (2)
 *   2   32   initiatorSignPublicKey
 *  34   32   initiatorAgreePublicKey
 *  66   32   ephemeralPublicKey
 *  98    4   signedPreKeyId            (uint32 BE)
 * 102    4   oneTimePreKeyId           (uint32 BE, SEM_OTPK = ausente)
 * 106    n   quadro selado da primeira mensagem
 *
 * NADA aqui e segredo: sao chaves publicas e ids. O texto vai no quadro, ja
 * cifrado. Um adversario que leia o envelope inteiro aprende que houve um
 * convite e para qual signed prekey — nunca o conteudo.
 *
 * A marca no byte 1 e `MessageType.Invite`, e e ela que separa convite de
 * quadro comum: um quadro de texto tem 0 ali, audio 1, controle 3, otp 4. O
 * tipo 2 fica reservado para este envelope e nunca sai de `sealFrame`.
 *
 * Nao ha campo de fingerprint de propósito. Ele viaja fora de banda, no
 * convite que a pessoa passa; e justamente por vir de fora que ele serve para
 * conferir o bundle do servidor (ver FingerprintMismatchError).
 */
import { MalformedFrameError, MessageType, type X3dhInitialMessage } from '@criptohomer/crypto-core'

export const INVITE_VERSION = 1
export const INVITE_HEADER_BYTES = 106

/** Sentinela de "esta mensagem nao consumiu one-time prekey". */
export const SEM_OTPK = 0xffffffff

const KEY_BYTES = 32

function writeUint32BE(value: number, target: Uint8Array, offset: number): void {
  target[offset] = (value >>> 24) & 0xff
  target[offset + 1] = (value >>> 16) & 0xff
  target[offset + 2] = (value >>> 8) & 0xff
  target[offset + 3] = value & 0xff
}

function readUint32BE(source: Uint8Array, offset: number): number {
  // `>>> 0` no fim nao e enfeite: em JS o `<<` trabalha em 32 bits COM SINAL,
  // entao `0xff << 24` da negativo e qualquer valor a partir de 2^31 — a
  // sentinela SEM_OTPK inclusive — voltaria errado.
  return (
    (((source[offset] ?? 0) << 24) |
      ((source[offset + 1] ?? 0) << 16) |
      ((source[offset + 2] ?? 0) << 8) |
      (source[offset + 3] ?? 0)) >>>
    0
  )
}

export interface Invite {
  readonly initialMessage: X3dhInitialMessage
  /** Quadro selado normal, exatamente como `sealFrame` devolveu. */
  readonly frame: Uint8Array
}

export function encodeInvite(invite: Invite): Uint8Array {
  const { initialMessage: m } = invite
  for (const [nome, chave] of [
    ['initiatorSignPublicKey', m.initiatorSignPublicKey],
    ['initiatorAgreePublicKey', m.initiatorAgreePublicKey],
    ['ephemeralPublicKey', m.ephemeralPublicKey],
  ] as const) {
    if (chave.length !== KEY_BYTES) {
      throw new MalformedFrameError(`${nome} deveria ter ${KEY_BYTES} bytes`)
    }
  }

  const out = new Uint8Array(INVITE_HEADER_BYTES + invite.frame.length)
  out[0] = INVITE_VERSION
  out[1] = MessageType.Invite
  out.set(m.initiatorSignPublicKey, 2)
  out.set(m.initiatorAgreePublicKey, 34)
  out.set(m.ephemeralPublicKey, 66)
  writeUint32BE(m.signedPreKeyId, out, 98)
  writeUint32BE(m.oneTimePreKeyId ?? SEM_OTPK, out, 102)
  out.set(invite.frame, INVITE_HEADER_BYTES)
  return out
}

export function decodeInvite(bytes: Uint8Array): Invite {
  if (bytes.length <= INVITE_HEADER_BYTES) {
    throw new MalformedFrameError('convite curto demais')
  }
  if (bytes[0] !== INVITE_VERSION) {
    throw new MalformedFrameError(`versao de convite ${String(bytes[0])} desconhecida`)
  }
  if (bytes[1] !== MessageType.Invite) {
    throw new MalformedFrameError('nao e um convite')
  }

  const otpk = readUint32BE(bytes, 102)
  return {
    initialMessage: {
      initiatorSignPublicKey: bytes.slice(2, 34),
      initiatorAgreePublicKey: bytes.slice(34, 66),
      ephemeralPublicKey: bytes.slice(66, 98),
      signedPreKeyId: readUint32BE(bytes, 98),
      // `exactOptionalPropertyTypes`: ausente e ausente, nao `undefined`.
      ...(otpk === SEM_OTPK ? {} : { oneTimePreKeyId: otpk }),
    },
    frame: bytes.slice(INVITE_HEADER_BYTES),
  }
}

/** Distingue convite de quadro comum sem tentar decodificar. */
export function isInvite(bytes: Uint8Array): boolean {
  return (
    bytes.length > INVITE_HEADER_BYTES &&
    bytes[0] === INVITE_VERSION &&
    bytes[1] === MessageType.Invite
  )
}
